import {
  useState, useEffect, useCallback, useContext,
} from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import WorldCanvas from '../../components/admin/world-editor/WorldCanvas';
import ToolPalette from '../../components/admin/world-editor/ToolPalette';
import useTileSheet from '../../components/admin/world-editor/useTileSheet';
import ZoneInspector from '../../components/admin/world-editor/ZoneInspector';
import { useWorldDraft } from '../../components/admin/world-editor/useWorldDraft';
import {
  getWorld, saveWorldDraft, publishWorld, discardWorldDraft,
} from '../../services/worlds';

/**
 * Drawing a world.
 *
 * SAVE AND PUBLISH ARE DIFFERENT BUTTONS, and the difference is the whole
 * shape of this page. Saving writes a draft and changes nothing anyone can
 * see; publishing pushes it into the running game and rebuilds the rooms.
 * Collapsing them into one would mean every stray brush stroke reached players
 * mid-edit — and a redeploy would put whatever was on screen live.
 *
 * A BROKEN WORLD STILL SAVES. Deleting the last spawn point before placing the
 * new one is normal, and losing the work over it would be absurd. Publish is
 * where it has to be loadable, and the server refuses there.
 */
function WorldEditor() {
  const { name } = useParams();
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();

  const {
    world, dirty, canUndo, selectedZone, load, paint, place, removeNear,
    addZone, updateZone, removeZone, zoneAt, selectZone, undo, markSaved,
  } = useWorldDraft();

  const [tool, setTool] = useState('paint');
  const [layer, setLayer] = useState('walls');
  const [brushGid, setBrushGid] = useState(3);
  const [zoom, setZoom] = useState(12);
  const [showUnreachable, setShowUnreachable] = useState(true);

  const [meta, setMeta] = useState(null);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showArt, setShowArt] = useState(true);

  // Loaded once here and shared by the palette and the canvas — one fetch, and
  // no way for the two to disagree about which sheet they are showing.
  const sheet = useTileSheet(meta?.tileset);

  const refresh = useCallback(async () => {
    try {
      const payload = await getWorld(token, name);
      load(payload);
      setMeta({
        hasDraft: payload.hasDraft,
        enabled: payload.enabled,
        // Not part of `world`: `useWorldDraft` owns geometry and undo, and the
        // tileset is neither — it should not be restorable by pressing undo.
        tileset: payload.tileset || null,
        warnings: payload.warnings || [],
      });
    } catch (error) {
      setStatus({ error: error.message });
    }
  }, [token, name, load]);

  useEffect(() => { refresh(); }, [refresh]);

  // Leaving with unsaved work loses it — the draft only exists on the server
  // once it is saved. The browser's own prompt is the only one that fires on a
  // tab close, so it is worth the ugliness.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const save = async () => {
    setBusy(true);
    setStatus(null);
    try {
      const result = await saveWorldDraft(token, name, world);
      markSaved();
      setMeta((m) => ({ ...m, hasDraft: true, warnings: result.warnings || [] }));
      setStatus(result.problem
        // Saved, but it would not load. Said plainly rather than as an error,
        // because the save DID work and the work is safe.
        ? { warn: `Saved as a draft. It will not publish yet: ${result.problem}` }
        : { ok: 'Saved as a draft. Publish when you want players to see it.' });
    } catch (error) {
      setStatus({ error: error.message });
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    setBusy(true);
    setStatus(null);
    try {
      // Save first so publishing never leaves a change behind on screen that
      // the server never heard about.
      if (dirty) {
        await saveWorldDraft(token, name, world);
        markSaved();
      }

      const result = await publishWorld(token, name);
      setMeta((m) => ({ ...m, hasDraft: false, warnings: result.warnings || [] }));
      setStatus({
        ok: result.moved
          ? `Published. ${result.moved} player(s) were moved to a safe spot.`
          : 'Published.',
      });
    } catch (error) {
      setStatus({ error: error.message });
    } finally {
      setBusy(false);
    }
  };

  const discard = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Throw away every unpublished change to this world?')) return;

    setBusy(true);
    try {
      await discardWorldDraft(token, name);
      await refresh();
      setStatus({ ok: 'Draft discarded. Back to what players see.' });
    } catch (error) {
      setStatus({ error: error.message });
    } finally {
      setBusy(false);
    }
  };

  if (!world) {
    return (
      <div className="mx-auto max-w-6xl p-6">
        <p className="text-sm text-gray-500">{status?.error || 'Loading…'}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl p-6">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/admin/worlds" className="text-sm text-blue-500 hover:underline">← Worlds</Link>
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          {world.title || world.name}
        </h1>
        {!meta?.enabled && (
          <span className="rounded bg-gray-500/15 px-1.5 py-0.5 text-[11px] font-medium text-gray-500">
            off — players cannot get here
          </span>
        )}
        {(dirty || meta?.hasDraft) && (
          <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[11px] font-medium text-amber-500">
            {dirty ? 'unsaved changes' : 'draft not published'}
          </span>
        )}

        <div className="ml-auto flex gap-2">
          <button type="button" onClick={undo} disabled={!canUndo} className="rounded bg-gray-200 px-3 py-1 text-sm disabled:opacity-40 dark:bg-gray-800 dark:text-gray-200">
            Undo
          </button>
          <button type="button" onClick={save} disabled={busy || !dirty} className="rounded bg-gray-200 px-3 py-1 text-sm disabled:opacity-40 dark:bg-gray-800 dark:text-gray-200">
            Save draft
          </button>
          <button type="button" onClick={discard} disabled={busy || !meta?.hasDraft} className="rounded bg-gray-200 px-3 py-1 text-sm disabled:opacity-40 dark:bg-gray-800 dark:text-gray-200">
            Discard
          </button>
          <button type="button" onClick={publish} disabled={busy} className="rounded bg-emerald-600 px-3 py-1 text-sm text-white hover:bg-emerald-500 disabled:opacity-40">
            Publish
          </button>
        </div>
      </div>

      {status?.error && <p className="mt-3 text-sm text-red-500">{status.error}</p>}
      {status?.warn && <p className="mt-3 text-sm text-amber-500">{status.warn}</p>}
      {status?.ok && <p className="mt-3 text-sm text-emerald-500">{status.ok}</p>}
      {(meta?.warnings || []).map((w) => (
        <p key={w} className="mt-1 text-xs text-amber-500">{w}</p>
      ))}

      <div className="mt-4 grid gap-4 lg:grid-cols-[14rem_1fr]">
        <ToolPalette
          tool={tool}
          layer={layer}
          brushGid={brushGid}
          zoom={zoom}
          showUnreachable={showUnreachable}
          showArt={showArt}
          sheet={sheet}
          tileset={meta?.tileset}
          onTool={setTool}
          onLayer={setLayer}
          onBrush={setBrushGid}
          onZoom={setZoom}
          onToggleUnreachable={setShowUnreachable}
          onShowArt={setShowArt}
        />

        {tool === 'zone' && (
          <div className="lg:col-start-1 lg:row-start-2">
            <ZoneInspector
              zone={selectedZone}
              onChange={(patch) => updateZone(selectedZone.id, patch)}
              onRemove={removeZone}
            />
          </div>
        )}

        <div>
          <WorldCanvas
            world={world}
            tool={tool}
            brushGid={brushGid}
            layer={layer}
            zoom={zoom}
            showUnreachable={showUnreachable}
            showArt={showArt}
            sheet={sheet}
            tileset={meta?.tileset}
            onPaint={paint}
            onPlace={place}
            onPickAt={removeNear}
            selectedZoneId={selectedZone?.id || null}
            onZoneDrag={addZone}
            onZoneClick={(tx, ty) => {
              const found = zoneAt(tx, ty);
              selectZone(found?.id || null);
              return found;
            }}
          />
          <p className="mt-2 text-xs text-gray-500">
            {world.widthTiles}
            ×
            {world.heightTiles}
            {' tiles · '}
            {world.spawns.length}
            {' spawn(s) · '}
            {world.portals.length}
            {' portal(s) · '}
            {world.signs.length}
            {' sign(s) · '}
            {world.zones.length}
            {' building(s)'}
            {' · '}
            <button type="button" onClick={() => navigate('/admin/worlds')} className="text-blue-500 hover:underline">
              back to the list
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

export default WorldEditor;
